/* import __COLOCATED_TEMPLATE__ from './manage-schedules.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { cached } from 'tracked-toolbox';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type ManageSchedulesApi from 'embercom/lib/reporting/manage-schedules-api';
import type ScheduledExternalShare from 'embercom/models/reporting/scheduled-external-share';
import type DatasetExportSchedule from 'embercom/models/reporting/dataset-export-schedule';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type NotificationsService from 'embercom/services/notifications-service';
import type Store from '@ember-data/store';
import {
  getRolesData,
  getUsersData,
  getEmailsData,
} from 'embercom/utils/recipient-resolution-helpers';

export type SortDirection = 'asc' | 'desc';
export type SortableColumn = 'name' | 'report' | 'owner' | 'createdAt' | 'updatedAt';
export type TabValue = 'reports' | 'datasets';
export type ScheduleType = ScheduledExternalShare | DatasetExportSchedule;

interface TableColumn {
  label: string;
  valuePath: string;
  width?: string;
  minWidth?: string;
  isSortable?: boolean;
}

interface Args {
  model: ManageSchedulesApi;
}

export default class ManageSchedulesComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare store: Store;
  @service declare permissionsService: any;

  @tracked sortBy?: SortableColumn;
  @tracked sortDirection?: SortDirection;
  @tracked isScheduleDeliveryDrawerOpen = false;
  @tracked scheduleToEdit?: ScheduledExternalShare;
  @tracked selectedTab: TabValue = 'reports';
  @tracked datasetScheduleToEdit?: DatasetExportSchedule;
  @tracked isDatasetScheduleDrawerOpen = false;

  get api(): ManageSchedulesApi {
    return this.args.model;
  }

  get sortState() {
    return {
      valuePath: this.sortBy,
      direction: this.sortDirection,
    };
  }

  get schedules(): ScheduleType[] {
    return this.selectedTab === 'datasets' ? this.api.datasetSchedules : this.api.schedules;
  }

  isReportSchedule(schedule: ScheduleType): schedule is ScheduledExternalShare {
    return 'reportId' in schedule;
  }

  isDatasetSchedule(schedule: ScheduleType): schedule is DatasetExportSchedule {
    return 'exportConfig' in schedule;
  }

  @cached
  get tableColumns(): TableColumn[] {
    let isSortingEnabled = this.selectedTab === 'reports';
    let baseColumns = this.getBaseColumns(isSortingEnabled);
    let tabSpecificColumns = this.getTabSpecificColumns();
    let commonColumns = this.getCommonColumns(isSortingEnabled);

    return [...baseColumns, ...tabSpecificColumns, ...commonColumns];
  }

  private getBaseColumns(isSortingEnabled: boolean): TableColumn[] {
    return [
      {
        label: this.intl.t('reporting.manage-schedules.table.name'),
        valuePath: 'name',
        width: '25%',
        minWidth: '260px',
        isSortable: isSortingEnabled,
      },
      {
        label: this.intl.t(
          this.selectedTab === 'datasets'
            ? 'reporting.manage-schedules.table.dataset'
            : 'reporting.manage-schedules.table.report',
        ),
        valuePath: 'report',
        width: '20%',
        minWidth: '220px',
        isSortable: isSortingEnabled,
      },
    ];
  }

  private getTabSpecificColumns(): TableColumn[] {
    if (this.selectedTab === 'reports') {
      return [
        {
          label: this.intl.t('reporting.manage-schedules.table.recipients'),
          valuePath: 'recipients',
          width: '12%',
          minWidth: '120px',
          isSortable: false,
        },
      ];
    }
    return [];
  }

  private getCommonColumns(isSortingEnabled: boolean): TableColumn[] {
    return [
      {
        label: this.intl.t('reporting.manage-schedules.table.owned-by'),
        valuePath: 'owner',
        width: '13%',
        minWidth: '120px',
        isSortable: false,
      },
      {
        label: this.intl.t('reporting.manage-schedules.table.created'),
        valuePath: 'createdAt',
        width: '8%',
        minWidth: '120px',
        isSortable: isSortingEnabled,
      },
      {
        label: this.intl.t('reporting.manage-schedules.table.last-updated'),
        valuePath: 'updatedAt',
        width: '8%',
        minWidth: '120px',
        isSortable: isSortingEnabled,
      },
      {
        label: this.intl.t('reporting.manage-schedules.table.actions'),
        valuePath: 'actions',
        width: '4%',
        minWidth: '64px',
        isSortable: false,
      },
    ];
  }

  get totalSchedules() {
    return this.api.totalCount;
  }

  get datasetSchedulesCount() {
    return this.api.datasetSchedulesCount;
  }

  get emptyStateTitle() {
    return this.selectedTab === 'datasets'
      ? this.intl.t('reporting.manage-schedules.dataset-empty-state.title')
      : this.intl.t('reporting.manage-schedules.empty-state.title');
  }

  get emptyStateDescription() {
    return this.selectedTab === 'datasets'
      ? this.intl.t('reporting.manage-schedules.dataset-empty-state.description')
      : this.intl.t('reporting.manage-schedules.empty-state.description');
  }

  get isInitialLoad() {
    if (this.selectedTab === 'datasets') {
      return this.api.isLoadingDatasetSchedules && this.api.datasetSchedules.length === 0;
    }
    return this.api.isLoadingPage && this.schedules.length === 0;
  }

  get isLoadingMore() {
    return (
      this.selectedTab !== 'datasets' &&
      ((this.api.isLoadingPage && this.schedules.length > 0) || taskFor(this.sortUpdate).isRunning)
    );
  }

  @cached
  get actionMenuItemsForSchedule() {
    return (scheduleId: string) => {
      return [
        {
          items: [
            {
              text: this.intl.t('reporting.manage-schedules.actions.edit'),
              icon: 'edit',
              onSelectItem: () => this.editSchedule(scheduleId),
            },
            {
              text: this.intl.t('reporting.manage-schedules.actions.delete'),
              icon: 'trash',
              onSelectItem: () => this.deleteSchedule(scheduleId),
              isDestructive: true,
            },
          ],
        },
      ];
    };
  }

  @action
  editSchedule(scheduleId: string) {
    if (!this.permissionsService.currentAdminCan('can_export_csv')) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal('can_export_csv');
      return;
    }

    let schedule = this.schedules.find((s) => s.id === scheduleId);
    if (schedule) {
      if (this.isDatasetSchedule(schedule)) {
        this.datasetScheduleToEdit = schedule;
        this.isDatasetScheduleDrawerOpen = true;
      } else if (this.isReportSchedule(schedule)) {
        this.scheduleToEdit = schedule;
        this.isScheduleDeliveryDrawerOpen = true;
      }
    }
  }

  @action
  closeScheduleDeliveryDrawer() {
    this.isScheduleDeliveryDrawerOpen = false;
    this.scheduleToEdit = undefined;
  }

  @action
  closeDatasetScheduleDrawer() {
    this.isDatasetScheduleDrawerOpen = false;
    this.datasetScheduleToEdit = undefined;
  }

  @action
  handleScheduleUpdated(updatedScheduleData: ScheduledExternalShare) {
    this.api.updateScheduleInList(updatedScheduleData);
  }

  @action
  async deleteSchedule(scheduleId: string) {
    if (!this.permissionsService.currentAdminCan('can_export_csv')) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal('can_export_csv');
      return;
    }

    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('reporting.manage-schedules.delete-confirm.title'),
      body: this.intl.t('reporting.manage-schedules.delete-confirm.body'),
      primaryButtonType: 'primary-destructive',
      confirmButtonText: this.intl.t('reporting.manage-schedules.delete-confirm.confirm-button'),
    });

    if (!confirmed) {
      return;
    }

    let schedule = this.schedules.find((s) => s.id === scheduleId)!;

    try {
      if (this.isDatasetSchedule(schedule)) {
        await this.deleteDatasetSchedule(schedule, scheduleId);
      } else {
        await this.deleteReportSchedule(scheduleId);
      }
    } catch (error) {
      this.notificationsService.notifyError(this.intl.t('reporting.manage-schedules.delete-error'));
    }
  }

  private async deleteDatasetSchedule(schedule: DatasetExportSchedule, scheduleId: string) {
    await schedule.destroyRecord();
    this.api.datasetSchedules = this.api.datasetSchedules.filter((s) => s.id !== scheduleId);
    this.notificationsService.notifyConfirmation(
      this.intl.t('reporting.data-export.schedule-export.manage-schedule.delete-success-message'),
    );
  }

  private async deleteReportSchedule(scheduleId: string) {
    await this.api.deleteSchedule(scheduleId);
    this.notificationsService.notifyConfirmation(
      this.intl.t('reporting.manage-schedules.delete-success'),
    );
  }

  @restartableTask
  *sortUpdate(sortBy: SortableColumn, sortDirection: SortDirection): TaskGenerator<void> {
    yield this.api.sortUpdate(sortBy, sortDirection);
  }

  @action
  onSort(clickedValuePath: string): void {
    if (clickedValuePath === this.sortBy) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = clickedValuePath as SortableColumn;
      this.sortDirection = 'desc';
    }

    taskFor(this.sortUpdate).perform(this.sortBy, this.sortDirection);
  }

  @action
  async loadMore() {
    if (this.selectedTab !== 'datasets' && !this.api.lastPageHit && !this.api.isLoadingPage) {
      await this.api.loadPage();
    }
  }

  get hasMoreSchedules(): boolean {
    return this.selectedTab !== 'datasets' && !this.api.lastPageHit;
  }

  get recipientsDataForSchedule() {
    return (schedule: ScheduledExternalShare) => ({
      roles: getRolesData(this.store, this.intl, schedule),
      users: getUsersData(this.store, this.intl, schedule),
      emails: getEmailsData(this.intl, schedule),
    });
  }

  @action
  onTabChange(tab: TabValue) {
    this.selectedTab = tab;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::ManageSchedules': typeof ManageSchedulesComponent;
    'reporting/manage-schedules': typeof ManageSchedulesComponent;
  }
}
